import { Injectable } from "@angular/core";

import { environment } from "../../../../environments/environment";
import {
    CatalogExportFilters,
    ExportFormat,
    EXPORT_DOWNLOAD_FAILED,
    EXPORT_DOWNLOAD_NOT_AVAILABLE,
    SHARE_LINK_NOT_CONFIGURED,
} from "../../../shared/types/export-format.types";
import { catalogExportRequiresPost } from "../../../shared/utils/catalog-export.utils";

const STORAGE_KEY = "venduro_export_format";

@Injectable({
    providedIn: "root",
})
export class ExportDownloadService {
    getPreferredFormat(): ExportFormat {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === "csv" ? "csv" : "xlsx";
    }

    setPreferredFormat(format: ExportFormat): void {
        localStorage.setItem(STORAGE_KEY, format);
    }

    getApiBaseUrl(): string {
        const host = (environment.apiHost || "").replace(/\/$/, "");
        const port = environment.apiPort ? `:${environment.apiPort}` : "";
        return `${host}${port}`;
    }

    buildExportUrl(path: string, format: ExportFormat): string {
        const timestamp = Date.now();
        const separator = path.includes("?") ? "&" : "?";

        return `${this.getApiBaseUrl()}${path}${separator}cacheBust=${timestamp}&format=${format}`;
    }

    buildShareableUrl(path: string, format: ExportFormat): string {
        const token = environment.csvExportToken;
        if (!token) {
            throw new Error(SHARE_LINK_NOT_CONFIGURED);
        }

        const separator = path.includes("?") ? "&" : "?";
        const encodedToken = encodeURIComponent(token);

        return `${this.getApiBaseUrl()}${path}${separator}token=${encodedToken}&format=${format}`;
    }

    buildCatalogPath(
        supplyType: string,
        filters?: CatalogExportFilters | null,
        includeRetailPrices = false,
    ): string {
        const params = new URLSearchParams();
        params.set("supplyType", supplyType);
        if (includeRetailPrices) {
            params.set("includeRetailPrices", "true");
        }

        if (filters) {
            if (filters.facetValueIds?.length) {
                params.set("facetValueIds", filters.facetValueIds.join(","));
            }
            if (filters.collectionId != null && filters.collectionId !== "") {
                params.set("collectionId", String(filters.collectionId));
            }
            if (filters.term?.trim()) {
                params.set("term", filters.term.trim());
            }
            if (filters.supplyTypes?.length) {
                params.set("supplyTypes", filters.supplyTypes.join(","));
            }
            if (filters.supplierId != null && filters.supplierId !== "") {
                params.set("supplierId", String(filters.supplierId));
            }
            if (filters.supplierIds?.length) {
                params.set(
                    "supplierIds",
                    filters.supplierIds.map((id) => String(id)).join(","),
                );
            }
            if (filters.mov?.length) {
                params.set("mov", filters.mov.join(","));
            }
            if (filters.shippingDate?.length) {
                params.set("shippingDate", filters.shippingDate.join(","));
            }
        }

        return `/products/csv?${params.toString()}`;
    }

    buildSupplierShareableUrl(
        supplierId: string,
        format?: ExportFormat,
    ): string {
        const resolvedFormat = format ?? this.getPreferredFormat();
        return this.buildShareableUrl(
            `/products/csv/supplier/${supplierId}`,
            resolvedFormat,
        );
    }

    buildCatalogShareableUrl(
        supplyType: string,
        format?: ExportFormat,
        filters?: CatalogExportFilters | null,
        includeRetailPrices = false,
    ): string {
        const resolvedFormat = format ?? this.getPreferredFormat();
        return this.buildShareableUrl(
            this.buildCatalogPath(
                supplyType,
                filters,
                includeRetailPrices,
            ),
            resolvedFormat,
        );
    }

    async copyShareableLink(url: string): Promise<void> {
        await navigator.clipboard.writeText(url);
    }

    async copySupplierShareLink(
        supplierId: string,
        format?: ExportFormat,
    ): Promise<void> {
        const url = this.buildSupplierShareableUrl(supplierId, format);
        await this.copyShareableLink(url);
    }

    async copyCatalogShareLink(
        supplyType: string,
        format?: ExportFormat,
        filters?: CatalogExportFilters | null,
        includeRetailPrices = false,
    ): Promise<void> {
        const url = this.buildCatalogShareableUrl(
            supplyType,
            format,
            filters,
            includeRetailPrices,
        );
        await this.copyShareableLink(url);
    }

    async downloadShareable(options: {
        path: string;
        filenameBase: string;
        format?: ExportFormat;
    }): Promise<void> {
        const format = options.format ?? this.getPreferredFormat();
        const url = `${this.buildShareableUrl(options.path, format)}&cacheBust=${Date.now()}`;

        const response = await fetch(url, { method: "GET" });

        if (!response.ok) {
            throw new Error(
                response.status === 403
                    ? EXPORT_DOWNLOAD_NOT_AVAILABLE
                    : EXPORT_DOWNLOAD_FAILED,
            );
        }

        await this.triggerBlobDownload(response, options.filenameBase, format);
    }

    async downloadSupplierShareable(
        supplierId: string,
        filenameBase: string,
        format?: ExportFormat,
    ): Promise<void> {
        await this.downloadShareable({
            path: `/products/csv/supplier/${supplierId}`,
            filenameBase,
            format,
        });
    }

    async downloadCatalogShareable(
        supplyType: string,
        filenameBase: string,
        format?: ExportFormat,
        filters?: CatalogExportFilters | null,
        includeRetailPrices = false,
    ): Promise<void> {
        const resolvedFormat = format ?? this.getPreferredFormat();
        const usePost = catalogExportRequiresPost(filters);
        const authToken = localStorage.getItem("auth_token");

        if (authToken) {
            try {
                if (usePost) {
                    await this.downloadCatalogAuthenticatedPost({
                        supplyType,
                        filenameBase,
                        format: resolvedFormat,
                        filters,
                        includeRetailPrices,
                    });
                } else {
                    const catalogPath = this.buildCatalogPath(
                        supplyType,
                        filters,
                        includeRetailPrices,
                    );
                    const authenticatedPath = catalogPath.replace(
                        /^\/products\/csv/,
                        "/csv/products",
                    );
                    await this.downloadAuthenticated({
                        path: authenticatedPath,
                        filenameBase,
                        format: resolvedFormat,
                    });
                }
                return;
            } catch {
            }
        }

        if (usePost) {
            await this.downloadCatalogShareablePost({
                supplyType,
                filenameBase,
                format: resolvedFormat,
                filters,
                includeRetailPrices,
            });
            return;
        }

        await this.downloadShareable({
            path: this.buildCatalogPath(
                supplyType,
                filters,
                includeRetailPrices,
            ),
            filenameBase,
            format: resolvedFormat,
        });
    }

    async downloadAuthenticated(options: {
        path: string;
        filenameBase: string;
        format?: ExportFormat;
    }): Promise<void> {
        const format = options.format ?? this.getPreferredFormat();
        const url = this.buildExportUrl(options.path, format);
        const token = localStorage.getItem("auth_token");

        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error("You need to be signed in");
        }

        await this.triggerBlobDownload(response, options.filenameBase, format);
    }

    private async downloadCatalogAuthenticatedPost(options: {
        supplyType: string;
        filenameBase: string;
        format: ExportFormat;
        filters?: CatalogExportFilters | null;
        includeRetailPrices: boolean;
    }): Promise<void> {
        const token = localStorage.getItem("auth_token");
        const url = `${this.getApiBaseUrl()}/csv/products?cacheBust=${Date.now()}&format=${options.format}`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                supplyType: options.supplyType,
                format: options.format,
                includeRetailPrices: options.includeRetailPrices,
                filters: options.filters ?? null,
            }),
        });

        if (!response.ok) {
            throw new Error(
                response.status === 403
                    ? EXPORT_DOWNLOAD_NOT_AVAILABLE
                    : EXPORT_DOWNLOAD_FAILED,
            );
        }

        await this.triggerBlobDownload(
            response,
            options.filenameBase,
            options.format,
        );
    }

    private async downloadCatalogShareablePost(options: {
        supplyType: string;
        filenameBase: string;
        format: ExportFormat;
        filters?: CatalogExportFilters | null;
        includeRetailPrices: boolean;
    }): Promise<void> {
        const exportToken = environment.csvExportToken;
        if (!exportToken) {
            throw new Error(SHARE_LINK_NOT_CONFIGURED);
        }

        const url = `${this.getApiBaseUrl()}/products/csv?cacheBust=${Date.now()}&format=${options.format}&token=${encodeURIComponent(exportToken)}`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                supplyType: options.supplyType,
                format: options.format,
                includeRetailPrices: options.includeRetailPrices,
                filters: options.filters ?? null,
            }),
        });

        if (!response.ok) {
            throw new Error(
                response.status === 403
                    ? EXPORT_DOWNLOAD_NOT_AVAILABLE
                    : EXPORT_DOWNLOAD_FAILED,
            );
        }

        await this.triggerBlobDownload(
            response,
            options.filenameBase,
            options.format,
        );
    }

    private async triggerBlobDownload(
        response: Response,
        filenameBase: string,
        format: ExportFormat,
    ): Promise<void> {
        const blob = await response.blob();
        const extension = format === "csv" ? "csv" : "xlsx";
        let filename = `${filenameBase}.${extension}`;

        const contentDisposition = response.headers.get("content-disposition");
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);
            if (filenameMatch?.[1]) {
                filename = filenameMatch[1];
            }
        }

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }
}
